import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { Coffee } from "../types";
import { useCoffees } from "../hooks/useCoffees";
import { useRoasterColors, indexByRoaster, type RoasterStyle } from "../hooks/useRoasterColors";

type RoasterColorMap = Map<string, RoasterStyle>;

/**
 * A4 label sheet — 4-column grid sized for a printer that needs ~4mm margins
 * on every edge. Two paper presets:
 *   - 10row: 4×10 = 40 labels (29.7mm precut interval, default)
 *   - 12row: 4×12 = 48 labels (24.75mm precut interval, legacy)
 * First and last rows give up 4mm of their content height to the printer
 * margin so the precut grid still aligns. Columns are shrunk uniformly to
 * 50.5mm so the centerline (105mm) lands exactly on the col2/col3 cut.
 */

const PAGE_W = 210;
const PAGE_H = 297;
const COLS = 4;
const PRINTER_MARGIN = 4; // mm — top, bottom, left, right
const CELL_W = (PAGE_W - 2 * PRINTER_MARGIN) / COLS; // 50.5mm

type PaperKey = "10row" | "12row";
interface PaperPreset {
  rows: number;
  label: string;
  // Per-paper drift compensation: how far to shift the whole grid, in mm,
  // to counter measured printer offset (positive = right/down).
  // Measure once: print a sheet, eyeball the paper-edge → ink gap on each
  // side, and set shiftX = (left_gap - right_gap) / 2 etc.
  shiftX?: number;
  shiftY?: number;
  // Per-column horizontal nudge, in mm, on top of shiftX. Lets us correct
  // non-uniform precut spacing or printer barrel distortion column-by-column.
  // Indexed [col0, col1, col2, col3]. Missing entries treated as 0.
  colShifts?: (number | undefined)[];
}
const PAPERS: Record<PaperKey, PaperPreset> = {
  // Measured May 2026 on real sticker paper:
  //   - whole grid drifts +1mm right, +0.5mm down → shiftX/shiftY compensate
  //   - col 0 prints too far right ("15g" overflows the right edge) → -2mm
  //   - col 3 prints too far left → +2mm
  //   - middle columns land correctly
  // Oct 2026: shifted 1.5mm right of the original (shiftX -1 → +0.5).
  "10row": { rows: 10, label: "4 × 10 (40 labels)", shiftX: 0.5, shiftY: 0.5, colShifts: [-2, 0, 0, 2] },
  "12row": { rows: 12, label: "4 × 12 (48 labels)" },
};
const DEFAULT_PAPER: PaperKey = "10row";
const PAPER_STORAGE_KEY = "labelSheet.paper";

interface PaperGeometry {
  rows: number;
  fullCellH: number;
  edgeCellH: number;
  shiftX: number;
  shiftY: number;
  colShifts: number[];
}
function geometryFor(paper: PaperKey): PaperGeometry {
  const preset = PAPERS[paper];
  const rows = preset.rows;
  const fullCellH = PAGE_H / rows;
  const colShifts = Array.from({ length: COLS }, (_, i) => preset.colShifts?.[i] ?? 0);
  return {
    rows,
    fullCellH,
    edgeCellH: fullCellH - PRINTER_MARGIN,
    shiftX: preset.shiftX ?? 0,
    shiftY: preset.shiftY ?? 0,
    colShifts,
  };
}

interface SheetItem {
  coffee: Coffee;
  copies: number;
}

export function LabelSheet() {
  const [search, setSearch] = useSearchParams();
  const { data } = useCoffees();
  const { data: roasterColors } = useRoasterColors();
  const colorMap = useMemo(() => indexByRoaster(roasterColors), [roasterColors]);
  const [items, setItems] = useState<SheetItem[]>([]);
  const [startCell, setStartCell] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [paper, setPaper] = useState<PaperKey>(() => {
    if (typeof window === "undefined") return DEFAULT_PAPER;
    const stored = window.localStorage.getItem(PAPER_STORAGE_KEY);
    return stored && stored in PAPERS ? (stored as PaperKey) : DEFAULT_PAPER;
  });
  useEffect(() => {
    try { window.localStorage.setItem(PAPER_STORAGE_KEY, paper); } catch { /* ignore */ }
  }, [paper]);
  const geom = geometryFor(paper);
  const { rows: ROWS, fullCellH: FULL_CELL_H, edgeCellH: EDGE_CELL_H } = geom;

  // Hydrate from ?ids=1,2,3 (each id once) the first time data arrives.
  useEffect(() => {
    if (!data || items.length > 0) return;
    const ids = (search.get("ids") || "")
      .split(",").map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length > 0) {
      const found = ids
        .map((id) => data.find((c) => c.id === id))
        .filter((c): c is Coffee => Boolean(c));
      setItems(found.map((coffee) => ({ coffee, copies: 4 })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const addAll = () => {
    if (!data) return;
    setItems(data.filter((c) => !c.archived).map((coffee) => ({ coffee, copies: 4 })));
  };
  const clear = () => setItems([]);

  const totalCells = ROWS * COLS;
  // Clamp startCell when paper switches to a smaller layout.
  useEffect(() => {
    if (startCell > totalCells - 1) setStartCell(Math.max(0, totalCells - 1));
  }, [totalCells, startCell]);
  const cells = useMemo(() => {
    const out: (Coffee | null)[] = Array(totalCells).fill(null);
    let i = startCell;
    for (const { coffee, copies } of items) {
      for (let n = 0; n < copies && i < totalCells; n++, i++) out[i] = coffee;
    }
    return out;
  }, [items, startCell, totalCells]);

  const placed = cells.filter(Boolean).length;
  const overflow = items.reduce((n, it) => n + it.copies, 0) - (totalCells - startCell);

  const setCopies = (idx: number, copies: number) => {
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, copies: Math.max(0, copies) } : it)));
  };
  const remove = (idx: number) => setItems((cur) => cur.filter((_, i) => i !== idx));

  const updateUrl = () => {
    if (items.length === 0) return setSearch({});
    setSearch({ ids: items.map((it) => it.coffee.id).join(",") });
  };
  useEffect(() => { updateUrl(); /* eslint-disable-next-line */ }, [items.length]);

  return (
    <div className="space-y-8">
      <header className="space-y-2 print:hidden">
        <div className="eyebrow">Print · A4 Sheet</div>
        <h1 className="font-display text-3xl">Label sheet</h1>
        <p className="text-sm text-paper-600 max-w-xl">
          {COLS} columns × {ROWS} rows = {totalCells} mini-labels per A4 sheet
          ({CELL_W.toFixed(2)} × {FULL_CELL_H.toFixed(2)} mm; first & last rows {EDGE_CELL_H.toFixed(2)} mm to fit a {PRINTER_MARGIN}mm printer margin).
        </p>
      </header>

      <section className="grid md:grid-cols-[1fr_520px] gap-10 items-start print:hidden">
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3 pb-4 border-b border-paper-200">
            <div className="flex flex-col">
              <span className="label">Start at cell</span>
              <input type="number" min={0} max={totalCells - 1} value={startCell}
                onChange={(e) => setStartCell(Math.max(0, Math.min(totalCells - 1, Number(e.target.value))))}
                className="bg-transparent border-0 border-b border-paper-300 text-sm py-1 w-24 focus:outline-none focus:border-ink"
              />
              <span className="text-[10px] text-paper-400 mt-1">0 = top-left · use to skip used stickers</span>
            </div>
            <div className="flex flex-col">
              <span className="label">Paper</span>
              <select
                className="bg-transparent border-0 border-b border-paper-300 text-sm py-1 focus:outline-none focus:border-ink"
                value={paper}
                onChange={(e) => setPaper(e.target.value as PaperKey)}
              >
                {(Object.keys(PAPERS) as PaperKey[]).map((k) => (
                  <option key={k} value={k}>{PAPERS[k].label}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={showNotes} onChange={(e) => setShowNotes(e.target.checked)} />
              Personal notes on labels
            </label>
            <div className="ml-auto flex gap-2">
              <button className="btn-ghost" onClick={addAll}>Add all coffees</button>
              <button className="btn-ghost" onClick={clear} disabled={items.length === 0}>Clear</button>
              <button className="btn-primary" onClick={() => window.print()} disabled={placed === 0}>Print</button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="py-16 text-center text-paper-500 border border-dashed border-paper-300">
              Choose coffees to add to this sheet.
              <div className="mt-3"><Link to="/browse" className="text-ink underline">Browse →</Link></div>
            </div>
          ) : (
            <ul className="border-t border-paper-200">
              {items.map((it, i) => (
                <li key={i} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center py-2 border-b border-paper-200 text-sm">
                  <div>
                    <div className="font-medium">{it.coffee.variety}</div>
                    <div className="text-xs text-paper-500">{it.coffee.country} · {it.coffee.roaster}</div>
                  </div>
                  <input type="number" min={0} value={it.copies}
                    onChange={(e) => setCopies(i, Number(e.target.value))}
                    className="bg-transparent border-0 border-b border-paper-300 text-sm py-1 w-16 text-right focus:outline-none focus:border-ink" />
                  <button className="text-paper-400 hover:text-red-700" onClick={() => remove(i)}>×</button>
                </li>
              ))}
              <li className="py-2 text-xs text-paper-500 flex justify-between">
                <span>Placed: <span className="tabular-nums text-ink">{placed}</span> / {totalCells}</span>
                {overflow > 0 ? <span className="text-red-700">Overflow: {overflow}</span> : null}
              </li>
            </ul>
          )}

          <CoffeePicker
            coffees={(data || []).filter((c) => !c.archived && !items.some((it) => it.coffee.id === c.id))}
            onPick={(coffee) => setItems((cur) => [...cur, { coffee, copies: 4 }])}
          />
        </div>

        <PreviewPanel cells={cells} showNotes={showNotes} geom={geom} colorMap={colorMap} />
      </section>

      {/* The actual print artefact, hidden in normal view, visible only in print preview */}
      <PrintableSheet cells={cells} showNotes={showNotes} geom={geom} colorMap={colorMap} />
    </div>
  );
}

function CoffeePicker({ coffees, onPick }: { coffees: Coffee[]; onPick: (c: Coffee) => void }) {
  const [q, setQ] = useState("");
  const filtered = q
    ? coffees.filter((c) => `${c.variety} ${c.country} ${c.roaster}`.toLowerCase().includes(q.toLowerCase()))
    : coffees;
  return (
    <details className="border-t border-paper-200 pt-4">
      <summary className="eyebrow cursor-pointer hover:text-ink">Add a coffee ({coffees.length} available)</summary>
      <div className="pt-3 space-y-2">
        <input className="input" placeholder="Filter…" value={q} onChange={(e) => setQ(e.target.value)} />
        <ul className="max-h-72 overflow-auto border border-paper-200">
          {filtered.map((c) => (
            <li key={c.id}>
              <button onClick={() => onPick(c)} className="w-full text-left px-3 py-2 hover:bg-paper-100 border-b border-paper-200 text-sm">
                <span className="font-medium">{c.variety}</span>
                <span className="text-paper-500"> · {c.country} · {c.roaster}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}

function PreviewPanel({ cells, showNotes, geom, colorMap }: { cells: (Coffee | null)[]; showNotes: boolean; geom: PaperGeometry; colorMap: RoasterColorMap }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);
  // Absolute scale, 1 = full A4. Default 0.6 (≈ 126mm wide A4 preview).
  const [scale, setScale] = useState(0.6);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const PX_PER_MM = 3.7795275591; // CSS 96dpi
  const pageWPx = PAGE_W * PX_PER_MM;
  const fitScale = containerW > 0 ? containerW / pageWPx : 0.6;

  return (
    <div className="space-y-2 max-w-full">
      <div className="eyebrow flex items-center justify-between gap-2">
        <span>Preview · {Math.round(scale * 100)}%</span>
        <div className="flex items-center gap-1 text-paper-700">
          <button type="button" aria-label="Zoom out"
            onClick={() => setScale((s) => Math.max(0.25, +(s - 0.1).toFixed(2)))}
            className="px-2 py-0.5 border border-paper-300 hover:bg-paper-100">−</button>
          <button type="button"
            onClick={() => setScale(+fitScale.toFixed(2))}
            className="px-2 py-0.5 border border-paper-300 hover:bg-paper-100">Fit</button>
          <button type="button" aria-label="Zoom in"
            onClick={() => setScale((s) => Math.min(2, +(s + 0.1).toFixed(2)))}
            className="px-2 py-0.5 border border-paper-300 hover:bg-paper-100">+</button>
        </div>
      </div>
      <div ref={containerRef} className="overflow-auto max-h-[85vh] border border-paper-200 bg-paper-50">
        <div
          className="border border-paper-300 bg-white shadow-sm"
          style={{ width: `${PAGE_W * scale}mm`, height: `${PAGE_H * scale}mm`, position: "relative" }}
        >
          <SheetGrid cells={cells} scale={scale} showNotes={showNotes} geom={geom} colorMap={colorMap} />
        </div>
      </div>
    </div>
  );
}

function PrintableSheet({ cells, showNotes, geom, colorMap }: { cells: (Coffee | null)[]; showNotes: boolean; geom: PaperGeometry; colorMap: RoasterColorMap }) {
  return (
    <div
      className="print-sheet hidden print:block"
      style={{ width: `${PAGE_W}mm`, height: `${PAGE_H}mm`, position: "relative", background: "white" }}
    >
      <SheetGrid cells={cells} scale={1} showNotes={showNotes} geom={geom} colorMap={colorMap} showBorders={false} />
    </div>
  );
}

function SheetGrid({ cells, scale, showNotes, geom, colorMap, showBorders = true }: {
  cells: (Coffee | null)[]; scale: number; showNotes: boolean; geom: PaperGeometry; colorMap: RoasterColorMap; showBorders?: boolean;
}) {
  const { rows: ROWS, fullCellH: FULL_CELL_H, edgeCellH: EDGE_CELL_H, shiftX, shiftY, colShifts } = geom;
  const bw = `${0.2 * scale}mm`;
  const stroke = `${bw} solid #111`;
  return (
    <>
      {cells.map((coffee, idx) => {
        const row = Math.floor(idx / COLS);
        const col = idx % COLS;
        const x = PRINTER_MARGIN + col * CELL_W + shiftX + (colShifts[col] || 0);
        let y: number;
        let h: number;
        if (row === 0) {
          y = PRINTER_MARGIN + shiftY;
          h = EDGE_CELL_H;
        } else if (row === ROWS - 1) {
          y = PRINTER_MARGIN + EDGE_CELL_H + (ROWS - 2) * FULL_CELL_H + shiftY;
          h = EDGE_CELL_H;
        } else {
          y = PRINTER_MARGIN + EDGE_CELL_H + (row - 1) * FULL_CELL_H + shiftY;
          h = FULL_CELL_H;
        }
        const style: React.CSSProperties = {
          position: "absolute",
          left: `${x * scale}mm`,
          top: `${y * scale}mm`,
          width: `${CELL_W * scale}mm`,
          height: `${h * scale}mm`,
          padding: `${1.5 * scale}mm ${2 * scale}mm`,
          boxSizing: "border-box",
          overflow: "hidden",
          fontSize: `${(scale === 1 ? 6.2 : 6.2 * scale)}pt`,
          lineHeight: 1.15,
          fontFamily: "Inter, system-ui, sans-serif",
          color: "#111",
          background: "white",
        };
        if (!coffee) {
          return <div key={idx} style={style} />;
        }
        // Cut guides only on filled cells, and only on screen — printing them
        // would make small printer offsets visible at the cut line.
        if (showBorders) {
          style.borderRight = stroke;
          style.borderBottom = stroke;
          if (col === 0) style.borderLeft = stroke;
          if (row === 0) style.borderTop = stroke;
        }
        return <Label key={idx} coffee={coffee} style={style} showNotes={showNotes} roasterColor={colorMap.get(coffee.roaster) || null} />;
      })}
    </>
  );
}

function processStyle(process: string): React.CSSProperties {
  const p = (process || "").trim().toLowerCase();
  // Honey + Natural use substring so subtypes ("White Honey", "Anaerobic
  // Natural") inherit the family colour. Washed is exact-only — compounds
  // like "Anaerobic Washed" are treated as experimental and fall through to
  // the "other" chip.
  if (p.includes("honey")) return { color: "#FF5C00", fontWeight: 700 };
  if (p.includes("natural")) return { color: "#006039", fontWeight: 700 };
  if (p === "washed") return { color: "#000099", fontWeight: 700 };
  return { background: "#341539", color: "#fff", fontWeight: 700, padding: "0.2mm 0.6mm" };
}

function Label({ coffee, style, showNotes, roasterColor }: { coffee: Coffee; style: React.CSSProperties; showNotes: boolean; roasterColor: RoasterStyle | null }) {
  const grades = (coffee.grade || "").split(",").map((s) => s.trim()).filter(Boolean);
  const procStyle: React.CSSProperties = {
    ...processStyle(coffee.process),
    WebkitPrintColorAdjust: "exact",
    printColorAdjust: "exact",
  };
  const roasterStyle: React.CSSProperties = roasterColor
    ? {
        background: roasterColor.bg,
        color: roasterColor.text,
        padding: "0.3mm 0.8mm",
        display: "inline-block",
        fontWeight: roasterColor.bold ? 700 : undefined,
        fontStyle: roasterColor.italic ? "italic" : undefined,
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }
    : { color: "#222" };
  return (
    <div style={style}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5mm" }}>
        <strong style={{ fontWeight: 600, fontSize: "1.15em", letterSpacing: "-0.01em" }}>
          {coffee.variety}
        </strong>
        <span style={{ fontSize: "0.85em", color: "#444" }}>
          <span style={procStyle}>{coffee.process}</span> {coffee.grams}g
        </span>
      </div>
      <div style={{ marginTop: "0.4mm" }}>
        <span style={{ fontWeight: 500 }}>{coffee.country}</span>
        {coffee.region ? `, ${coffee.region}` : ""}
        {coffee.masl ? ` ${coffee.masl} masl` : ""}
      </div>
      {(coffee.farm || coffee.farmer) ? (
        <div style={{ color: "#444" }}>
          {coffee.farm || ""}{coffee.farm && coffee.farmer ? " · " : ""}{coffee.farmer || ""}
        </div>
      ) : null}
      <div>
        <span style={roasterStyle}>
          {coffee.roaster}{coffee.roaster_city ? `, ${coffee.roaster_city}` : ""}
        </span>
      </div>
      {coffee.roast_date ? (
        <div style={{ color: "#1A1AC6", fontWeight: 700, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
          roast date: {fmtDate(coffee.roast_date)}
        </div>
      ) : null}
      {coffee.flavor_tags.length > 0 ? (
        <div style={{ color: "#444", fontStyle: "italic", marginTop: "0.4mm" }}>
          {coffee.flavor_tags.slice(0, 6).join(" · ")}
        </div>
      ) : null}
      {grades.length > 0 ? (
        <div style={{ marginTop: "0.4mm" }}>
          {grades.map((g) => (
            <span key={g} style={{
              display: "inline-block",
              border: "0.15mm solid #111", padding: "0 0.6mm",
              fontSize: "0.75em", marginRight: "0.6mm",
              textTransform: "uppercase", letterSpacing: "0.06em",
            }}>{g}</span>
          ))}
        </div>
      ) : null}
      {showNotes && coffee.personal_notes ? (
        <div style={{ color: "#666", fontSize: "0.85em", marginTop: "0.3mm" }}>{coffee.personal_notes}</div>
      ) : null}
    </div>
  );
}

function fmtDate(iso: string): string {
  // Convert 2026-03-27 → 27.03.26 (matches the user's existing label format)
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1].slice(2)}`;
}
